// src/pages/PainelAgendamentos/CamposTecnico.jsx
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

export default function CamposTecnico({
  colaborador,
  setColaborador,
  tecnico,
  setTecnico,
  tipoServico,
  setTipoServico,
}) {
  const [openSelect, setOpenSelect] = useState(false);
  const [openServico, setOpenServico] = useState(false);
  const [usuarios, setUsuarios] = useState([]); // técnicos
  const [opcoesServico, setOpcoesServico] = useState([]);

  const refSelect = useRef(null);
  const refServico = useRef(null);

  // 🔹 Estilos iguais para todos
  const inputBase = `
    peer w-full h-10 px-2 border-b border-gray-300
    focus:border-blue-600 focus:outline-none bg-transparent
    text-base text-gray-900 placeholder-transparent
  `;

  const labelBase = (v) => `
    absolute left-2 transition-all duration-200 pointer-events-none
    ${v ? "-top-4 text-sm font-bold text-gray-900" : "top-2 text-base text-gray-400"}
    peer-focus:-top-4 peer-focus:text-sm peer-focus:font-bold peer-focus:text-gray-900
  `;

  // 🔹 Fecha dropdowns ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (refSelect.current && !refSelect.current.contains(e.target)) {
        setOpenSelect(false);
      }
      if (refServico.current && !refServico.current.contains(e.target)) {
        setOpenServico(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // 🔹 Buscar técnicos
  useEffect(() => {
    const fetchUsuarios = async () => {
      try {
        const resp = await fetch(`${API_URL}/usuarios/listar.php`);
        const data = await resp.json();
        if (data.status === "success") {
          setUsuarios(data.dados);
        } else {
          setUsuarios([]);
        }
      } catch (err) {
        console.error("Erro ao carregar usuários:", err);
        setUsuarios([]);
      }
    };
    fetchUsuarios();
  }, []);

  // 🔹 Buscar serviços
  useEffect(() => {
    const carregarServicos = async () => {
      try {
        const resp = await fetch(`${API_URL}/tipo-servico/listar.php`);
        if (!resp.ok) throw new Error(`HTTP error! status: ${resp.status}`);
        const json = await resp.json();
        if (json && Array.isArray(json.dados)) {
          setOpcoesServico(json.dados);
        } else {
          setOpcoesServico([]);
        }
      } catch (error) {
        console.error("Erro ao carregar serviços:", error);
        setOpcoesServico([]);
      }
    };
    carregarServicos();
  }, []);

  // 🔹 Filtra usuários conforme digitado
  const usuariosFiltrados = usuarios.filter((u) =>
    u.nome.toLowerCase().includes(tecnico.toLowerCase())
  );

  // 🔹 Valida técnico
  const validarTecnico = () => {
    const valido = usuarios.some(
      (u) => u.nome.toLowerCase() === tecnico.toLowerCase()
    );
    if (!valido) setTecnico("");
  };

  return (
    <div className="flex flex-row gap-6 mt-6 w-full">
      {/* Colaborador */}
      <div className="relative flex-1 min-w-[220px]">
        <input
          type="text"
          value={colaborador}
          onChange={(e) => setColaborador(e.target.value)}
          placeholder=" "
          className={inputBase}
        />
        <label className={labelBase(colaborador)}>Colaborador</label>
      </div>

      {/* Técnico Responsável */}
      <div className="relative flex-1 min-w-[220px] max-w-[280px]" ref={refSelect}>
        <input
          type="text"
          value={tecnico}
          onChange={(e) => {
            setTecnico(e.target.value);
            setOpenSelect(true);
          }}
          onBlur={validarTecnico}
          placeholder=" "
          className={inputBase + " cursor-text"}
        />
        <label className={labelBase(tecnico)}>Técnico Responsável</label>

        <ChevronDown
          size={16}
          onClick={() => setOpenSelect((prev) => !prev)}
          className={`absolute right-1 top-3 cursor-pointer transition-transform ${
            openSelect ? "rotate-180" : "rotate-0"
          }`}
        />

        <AnimatePresence>
          {openSelect && (
            <motion.ul
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.2 }}
              className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 
                         rounded-md shadow-md z-10 max-h-52 overflow-y-auto"
            >
              {usuariosFiltrados.length > 0 ? (
                usuariosFiltrados.map((u) => (
                  <li
                    key={u.id}
                    onMouseDown={() => {
                      setTecnico(u.nome);
                      setOpenSelect(false);
                    }}
                    className="px-3 py-2 text-sm text-gray-700 hover:bg-blue-100 cursor-pointer"
                  >
                    {u.nome}
                  </li>
                ))
              ) : (
                <li className="px-3 py-2 text-sm text-gray-500 italic">
                  Nenhum usuário encontrado
                </li>
              )}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>

      {/* Tipo de Serviço */}
      <div className="relative flex-1 min-w-[220px]" ref={refServico}>
        <input
          type="text"
          value={tipoServico}
          onClick={() => setOpenServico((p) => !p)}
          readOnly
          placeholder=" "
          className={inputBase + " cursor-text"}
        />
        <label className={labelBase(tipoServico)}>Tipo de Serviço</label>

        <ChevronDown
          size={16}
          className={`absolute right-1 top-3 transition-transform ${
            openServico ? "rotate-180" : "rotate-0"
          }`}
        />

        <AnimatePresence>
          {openServico && (
            <motion.ul
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.2 }}
              className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 
                         rounded-md shadow-md z-10 max-h-52 overflow-y-auto"
            >
              {opcoesServico.length > 0 ? (
                opcoesServico.map((s) => (
                  <li
                    key={s.id}
                    onMouseDown={() => {
                      setTipoServico(s.nome);
                      setOpenServico(false);
                    }}
                    className="px-3 py-2 text-sm text-gray-700 hover:bg-blue-100 cursor-pointer"
                  >
                    {s.nome}
                  </li>
                ))
              ) : (
                <li className="px-3 py-2 text-sm text-gray-500 italic">
                  Nenhum serviço encontrado
                </li>
              )}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
